import React, { useState, useEffect, useCallback } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const STATE_TIMEZONES = {
  AL:'Central',AK:'Alaska',AZ:'Mountain',AR:'Central',CA:'Pacific',CO:'Mountain',
  CT:'Eastern',DE:'Eastern',FL:'Eastern',GA:'Eastern',HI:'Hawaii',ID:'Mountain',
  IL:'Central',IN:'Eastern',IA:'Central',KS:'Central',KY:'Eastern',LA:'Central',
  ME:'Eastern',MD:'Eastern',MA:'Eastern',MI:'Eastern',MN:'Central',MS:'Central',
  MO:'Central',MT:'Mountain',NE:'Central',NV:'Pacific',NH:'Eastern',NJ:'Eastern',
  NM:'Mountain',NY:'Eastern',NC:'Eastern',ND:'Central',OH:'Eastern',OK:'Central',
  OR:'Pacific',PA:'Eastern',RI:'Eastern',SC:'Eastern',SD:'Central',TN:'Central',
  TX:'Central',UT:'Mountain',VT:'Eastern',VA:'Eastern',WA:'Pacific',WV:'Eastern',
  WI:'Central',WY:'Mountain',DC:'Eastern',
};

const FIPS_TO_STATE = {
  '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE',
  '11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA',
  '20':'KS','21':'KY','22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN',
  '28':'MS','29':'MO','30':'MT','31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM',
  '36':'NY','37':'NC','38':'ND','39':'OH','40':'OK','41':'OR','42':'PA','44':'RI',
  '45':'SC','46':'SD','47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA',
  '54':'WV','55':'WI','56':'WY',
};

const STATE_NAMES = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',
  CT:'Connecticut',DE:'Delaware',DC:'Washington DC',FL:'Florida',GA:'Georgia',HI:'Hawaii',
  ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',
  ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',
  MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',
  NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
  OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',
  TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',
  WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',
};

const STATUS_COLORS = {
  inactive: '#333',
  working: '#FF8C00',
  completed: '#22c55e',
};

export const USAMap = ({ apiUrl, token }) => {
  const [stateData, setStateData] = useState({});
  const [tooltip, setTooltip] = useState(null);
  const [editState, setEditState] = useState(null);
  const [editRestaurants, setEditRestaurants] = useState('');
  const [editStatus, setEditStatus] = useState('inactive');
  const [editAppsSold, setEditAppsSold] = useState('');
  const [editMonthlyRate, setEditMonthlyRate] = useState('');

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const fetchStates = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/api/admin/states`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const map = {};
      data.forEach(s => { map[s.code] = s; });
      setStateData(map);
    } catch (e) { console.error(e); }
  }, [apiUrl, token]);

  useEffect(() => { fetchStates(); }, [fetchStates]);

  const getStateStatus = (code) => stateData[code]?.status || 'inactive';
  const getStateRestaurants = (code) => stateData[code]?.restaurants || 0;
  const getStateColor = (code) => STATUS_COLORS[getStateStatus(code)];

  const openStateEditor = (code) => {
    setEditState(code);
    setEditRestaurants(String(getStateRestaurants(code)));
    setEditStatus(getStateStatus(code));
    setEditAppsSold(String(stateData[code]?.apps_sold || 0));
    setEditMonthlyRate(String(stateData[code]?.monthly_rate || 199));
  };

  const handleSaveState = async () => {
    if (!editState) return;
    await fetch(`${apiUrl}/api/admin/states/${editState}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ status: editStatus, restaurants: parseInt(editRestaurants) || 0, apps_sold: parseInt(editAppsSold) || 0, monthly_rate: parseFloat(editMonthlyRate) || 0 }),
    });
    setEditState(null);
    fetchStates();
  };

  const totalRestaurants = Object.values(stateData).reduce((sum, s) => sum + (s.restaurants || 0), 0);
  const totalAppsSold = Object.values(stateData).reduce((sum, s) => sum + (s.apps_sold || 0), 0);
  const totalMRR = Object.values(stateData).reduce((sum, s) => sum + ((s.apps_sold || 0) * (s.monthly_rate || 0)), 0);
  const totalARR = totalMRR * 12;
  const activeStates = Object.values(stateData).filter(s => s.status === 'working').length;
  const completedStates = Object.values(stateData).filter(s => s.status === 'completed').length;
  const totalTAM = totalRestaurants * 3564;

  const fmtMoney = (n) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n.toLocaleString()}`;

  return (
    <div data-testid="usa-map-section" style={{ background: '#111', borderTop: '3px solid #FFD700', padding: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h2 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.5rem', color: '#FFD700', letterSpacing: '0.03em' }}>
            Territory Map
          </h2>
          {totalMRR > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <div style={{ padding: '4px 10px', background: '#22c55e15', border: '1.5px solid #22c55e', borderRadius: '4px', fontFamily: "'Bangers', cursive", fontSize: '1rem', color: '#22c55e' }}>
                MRR {fmtMoney(totalMRR)}
              </div>
              <div style={{ padding: '4px 10px', background: '#6A0DAD15', border: '1.5px solid #6A0DAD', borderRadius: '4px', fontFamily: "'Bangers', cursive", fontSize: '1rem', color: '#6A0DAD' }}>
                ARR {fmtMoney(totalARR)}
              </div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ padding: '4px 12px', background: '#FFD700', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.85rem', color: '#000' }}>
            {totalRestaurants.toLocaleString()} RESTAURANTS
          </div>
          {totalAppsSold > 0 && (
            <div style={{ padding: '4px 12px', background: '#22c55e', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.85rem', color: '#000' }}>
              {totalAppsSold} APPS SOLD
            </div>
          )}
          <div style={{ padding: '4px 12px', background: '#FF8C00', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.85rem', color: '#000' }}>
            {activeStates} ACTIVE
          </div>
          <div style={{ padding: '4px 12px', background: '#22c55e', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.85rem', color: '#000' }}>
            {completedStates} COMPLETE
          </div>
          {totalRestaurants > 0 && (
            <div style={{ padding: '4px 12px', background: '#6A0DAD', border: '2px solid #000', borderRadius: '4px', fontWeight: 800, fontSize: '0.85rem', color: '#fff' }}>
              TAM ${totalTAM >= 1000000 ? `${(totalTAM / 1000000).toFixed(1)}M` : totalTAM >= 1000 ? `${(totalTAM / 1000).toFixed(0)}K` : totalTAM.toLocaleString()}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem', fontSize: '0.8rem', fontWeight: 600 }}>
        {[
          { color: '#333', label: 'Inactive' },
          { color: '#FF8C00', label: 'Active (Working)' },
          { color: '#22c55e', label: 'Complete' },
        ].map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: l.color, border: '1px solid #555' }} />
            <span style={{ color: '#aaa' }}>{l.label}</span>
          </div>
        ))}
        <span style={{ color: '#666', fontStyle: 'italic' }}>Click any state to set status & restaurants</span>
      </div>

      {/* Map */}
      <div style={{ background: '#0a0a0a', borderRadius: '8px', border: '2px solid #222', position: 'relative', overflow: 'hidden' }}>
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 1000 }}
          style={{ width: '100%', height: 'auto' }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map(geo => {
                const fips = geo.id;
                const code = FIPS_TO_STATE[fips];
                if (!code) return null;
                const color = getStateColor(code);
                const restaurants = getStateRestaurants(code);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => openStateEditor(code)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      openStateEditor(code);
                    }}
                    onMouseEnter={() => setTooltip({ code, name: STATE_NAMES[code], tz: STATE_TIMEZONES[code], restaurants })}
                    onMouseLeave={() => setTooltip(null)}
                    style={{
                      default: { fill: color, stroke: '#0a0a0a', strokeWidth: 1, outline: 'none', cursor: 'pointer', transition: 'fill 0.2s' },
                      hover: { fill: color === '#333' ? '#555' : color, stroke: '#FFD700', strokeWidth: 2, outline: 'none', cursor: 'pointer', filter: 'brightness(1.3)' },
                      pressed: { fill: color, stroke: '#FFD700', strokeWidth: 2, outline: 'none' },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>

        {/* Tooltip */}
        {tooltip && (
          <div style={{
            position: 'absolute', top: '1rem', left: '1rem',
            background: '#1a1a1a', border: '2px solid #FFD700', borderRadius: '8px',
            padding: '0.75rem 1rem', zIndex: 10, minWidth: '180px',
            boxShadow: '4px 4px 0px 0px #FFD700',
          }}>
            <div style={{ fontFamily: "'Bangers', cursive", fontSize: '1.25rem', color: '#FFD700', letterSpacing: '0.03em' }}>
              {tooltip.name}
            </div>
            <div style={{ color: '#aaa', fontSize: '0.8rem', fontWeight: 600, marginTop: '0.25rem' }}>
              {tooltip.code} | {tooltip.tz} Time
            </div>
            <div style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 700, marginTop: '0.25rem' }}>
              {tooltip.restaurants} Restaurants
            </div>
            <div style={{ marginTop: '0.25rem' }}>
              <span style={{
                padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase',
                background: STATUS_COLORS[getStateStatus(tooltip.code)] + '30',
                color: STATUS_COLORS[getStateStatus(tooltip.code)],
                border: `1px solid ${STATUS_COLORS[getStateStatus(tooltip.code)]}`,
              }}>
                {getStateStatus(tooltip.code)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Edit Restaurants Modal */}
      {editState && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.8)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setEditState(null); }}>
          <div style={{ width: '350px', background: '#1a1a1a', border: '3px solid #FFD700', borderRadius: '12px', boxShadow: '6px 6px 0px 0px #FFD700', padding: '1.5rem' }}>
            <h3 style={{ fontFamily: "'Bangers', cursive", fontSize: '1.5rem', color: '#FFD700', marginBottom: '1.25rem', letterSpacing: '0.03em' }}>
              {STATE_NAMES[editState]} ({editState})
            </h3>

            {/* Status */}
            <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#aaa', textTransform: 'uppercase', display: 'block', marginBottom: '0.5rem' }}>Status</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {[
                { value: 'inactive', label: 'Inactive', color: '#333' },
                { value: 'working', label: 'Working', color: '#FF8C00' },
                { value: 'completed', label: 'Complete', color: '#22c55e' },
              ].map(opt => (
                <button key={opt.value} onClick={() => setEditStatus(opt.value)} style={{
                  flex: 1, padding: '0.6rem', border: editStatus === opt.value ? `3px solid ${opt.color}` : '2px solid #333',
                  borderRadius: '6px', background: editStatus === opt.value ? `${opt.color}25` : '#111',
                  color: editStatus === opt.value ? opt.color : '#666', fontWeight: 800, fontSize: '0.8rem',
                  cursor: 'pointer', textTransform: 'uppercase',
                }}>
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Restaurants */}
            <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#aaa', textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem' }}>
              Total Restaurants
            </label>
            <input
              type="number"
              value={editRestaurants}
              onChange={(e) => setEditRestaurants(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', background: '#111', border: '2px solid #333', borderRadius: '6px', color: '#fff', fontSize: '1.25rem', outline: 'none', boxSizing: 'border-box', fontFamily: "'Bangers', cursive", letterSpacing: '0.03em' }}
              onFocus={(e) => e.target.style.borderColor = '#FFD700'}
              onBlur={(e) => e.target.style.borderColor = '#333'}
            />

            {/* Apps Sold */}
            <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#22c55e', textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem', marginTop: '0.75rem' }}>
              Apps Sold
            </label>
            <input
              type="number"
              value={editAppsSold}
              onChange={(e) => setEditAppsSold(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', background: '#111', border: '2px solid #22c55e50', borderRadius: '6px', color: '#22c55e', fontSize: '1.25rem', outline: 'none', boxSizing: 'border-box', fontFamily: "'Bangers', cursive", letterSpacing: '0.03em' }}
              onFocus={(e) => e.target.style.borderColor = '#22c55e'}
              onBlur={(e) => e.target.style.borderColor = '#22c55e50'}
            />

            {/* Monthly Rate */}
            <label style={{ fontWeight: 700, fontSize: '0.85rem', color: '#6A0DAD', textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem', marginTop: '0.75rem' }}>
              Monthly Rate ($/app)
            </label>
            <input
              type="number"
              value={editMonthlyRate}
              onChange={(e) => setEditMonthlyRate(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', background: '#111', border: '2px solid #6A0DAD50', borderRadius: '6px', color: '#6A0DAD', fontSize: '1.25rem', outline: 'none', boxSizing: 'border-box', fontFamily: "'Bangers', cursive", letterSpacing: '0.03em' }}
              onFocus={(e) => e.target.style.borderColor = '#6A0DAD'}
              onBlur={(e) => e.target.style.borderColor = '#6A0DAD50'}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={handleSaveState} style={{
                flex: 1, padding: '0.75rem', background: '#FFD700', color: '#000',
                border: '3px solid #000', borderRadius: '8px', fontWeight: 800,
                textTransform: 'uppercase', cursor: 'pointer', boxShadow: '3px 3px 0px 0px #000',
              }}>
                Save
              </button>
              <button onClick={() => setEditState(null)} style={{
                padding: '0.75rem 1.25rem', background: 'rgba(255,255,255,0.1)', color: '#fff',
                border: '2px solid #333', borderRadius: '8px', cursor: 'pointer', fontWeight: 700,
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
